import { AddCircleOutline, Delete } from '@mui/icons-material'
import { Box, useTheme } from '@mui/material'
import { GridActionsCellItem, GridColumns, GridNativeColTypes } from '@mui/x-data-grid-pro'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { MdEdit } from 'react-icons/md'
import { toast } from 'react-toastify'
import {
  IAisVessel,
  IAisVesselFormValues,
  invalidateAisVesselCache,
  resolveAisMapColor,
  useAddAisVessel,
  useDeleteAisVessel,
  useGetAisVessels,
  useUpdateAisVessel,
} from 'src/api/ais-vessels'
import { CommonPageLayout } from 'src/components/common/CommonPageLayout'
import GenericTable from 'src/components/table/GenericTable'
import { useCustomerCtx, useDialogCtx } from 'src/hooks/context-hooks'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import AisVesselForm from './AisVesselForm'

const AisVesselsPage = () => {
  const { customer } = useCustomerCtx()
  const theme = useTheme()
  const { showDialog, showConfirmDialog } = useDialogCtx()
  const { t: translate } = useTranslation(['ais-vessel', 'common', 'common-api-message', 'page-container'])
  const customerId = customer?.id as EntityId
  const { data, isLoading } = useGetAisVessels(customerId)
  const { mutateAsync, isLoading: isLoadingAdd } = useAddAisVessel(customerId)
  const { mutateAsync: updateMutate, isLoading: isLoadingUpdate } = useUpdateAisVessel(customerId)
  const { mutateAsync: deleteMutate } = useDeleteAisVessel(customerId)

  const showApiError = useCallback((error: any) => {
    toast.error(translate(error?.data || error?.response?.data || 'unExpectedError', { ns: 'common-api-message' }))
  }, [translate])

  const onAdd = useCallback(() => {
    showDialog(AisVesselForm, {
      title: translate('add'),
      opts: {
        maxWidth: 'sm',
        fullWidth: true,
      },
      componentProps: {
        onSubmit: async (values: IAisVesselFormValues, popCallback: () => void) => {
          await mutateAsync(values, {
            onSuccess: () => {
              toast.success(translate('addedSuccessfully'))
              invalidateAisVesselCache.list(customerId)
              popCallback()
            },
            onError: showApiError,
          })
        },
        isLoading: isLoadingAdd,
      },
    })
  }, [customerId, translate, mutateAsync, showDialog, isLoadingAdd, showApiError])

  const onUpdate = useCallback(
    (row: IAisVessel) => {
      showDialog(AisVesselForm, {
        title: translate('edit'),
        opts: {
          maxWidth: 'sm',
          fullWidth: true,
        },
        componentProps: {
          onSubmit: async (values: IAisVesselFormValues, popCallback: () => void) => {
            await updateMutate(
              { ...values, id: row.id },
              {
                onSuccess: () => {
                  toast.success(translate('updatedSuccessfully'))
                  invalidateAisVesselCache.list(customerId)
                  popCallback()
                },
                onError: showApiError,
              },
            )
          },
          initialValues: row,
          isUpdateForm: true,
          isLoading: isLoadingUpdate,
        },
      })
    },
    [customerId, translate, updateMutate, showDialog, isLoadingUpdate, showApiError],
  )

  const onDelete = useCallback(
    (row: IAisVessel) => {
      showConfirmDialog(
        translate('delete-title'),
        translate('delete-content'),
        {
          acceptText: translate('yes', { ns: 'common' }),
          cancelText: translate('no', { ns: 'common' }),
        },
        async pop => {
          await deleteMutate(row.id, {
            onSuccess: () => {
              toast.success(translate('deletedSuccessfully'))
              invalidateAisVesselCache.list(customerId)
              pop()
            },
            onError: showApiError,
          })
        },
      )
    },
    [customerId, translate, deleteMutate, showConfirmDialog, showApiError],
  )

  const columns: GridColumns = useMemo(
    () => [
      {
        field: 'mmsi',
        flex: 1,
        headerName: translate('mmsi', { ns: 'ais-vessel' }),
      },
      {
        field: 'name',
        flex: 1,
        headerName: translate('boat-name', { ns: 'ais-vessel' }),
        cellClassName: 'font-weight-semi-bold',
      },
      {
        field: 'callSign',
        flex: 1,
        headerName: translate('call-sign', { ns: 'ais-vessel' }),
        valueGetter: params => params.row.callSign || '-',
      },
      {
        field: 'color',
        width: 180,
        headerName: translate('color', { ns: 'ais-vessel' }),
        sortable: false,
        renderCell: params => {
          const color = resolveAisMapColor(params.row.color)
          return (
            <Box display="flex" alignItems="center" gap={1}>
              <Box
                width={20}
                height={20}
                borderRadius="4px"
                border={`1px solid ${theme.palette.divider}`}
                bgcolor={color}
              />
              {color}
            </Box>
          )
        },
      },
      {
        field: 'actions',
        type: 'actions' as GridNativeColTypes,
        width: 10,
        getActions: ({ row }) => [
          <GridActionsCellItem
            key="edit"
            label={translate('edit', { ns: 'common' })}
            icon={<MdEdit size={24} />}
            onClick={() => onUpdate(row)}
            color="primary"
            showInMenu
          />,
          <GridActionsCellItem
            key="delete"
            label={translate('delete', { ns: 'common' })}
            icon={<Delete fontSize="medium" />}
            onClick={() => onDelete(row)}
            color="primary"
            showInMenu
          />,
        ],
      },
    ],
    [translate, theme, onUpdate, onDelete],
  )

  return (
    <CommonPageLayout
      titleSection={{ title: translate('ais-vessels', { ns: 'page-container' }) }}
      topAction={[
        {
          buttonText: translate('ais-vessel', { ns: 'page-container' }),
          buttonIcon: <AddCircleOutline />,
          onClick: onAdd,
          variant: 'outlined',
          shadowWeight: null,
          border: 0,
        },
      ]}
    >
      <Box padding="8px" bgcolor={getThemeColor(theme, MUIThemeColors.white)} borderRadius="4px">
        <GenericTable
          id="customer-ais-vessels"
          data={!isLoading && data ? data : []}
          columns={columns}
          onRowClick={({ row }) => onUpdate(row)}
          autoHeight={false}
          height={window.innerHeight - 230 + 'px'}
          getRowId={row => row.id}
        />
      </Box>
    </CommonPageLayout>
  )
}

export default AisVesselsPage
